import React, { createContext, useContext, useEffect, useState } from 'react';
import { getCurrentUser, signOut, fetchUserAttributes, updateUserAttributes } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import logger from '../services/logger';
import { v4 as uuidv4 } from 'uuid';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Listen for authentication events
    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      const { event } = payload;
      console.log('Auth event:', event);
      if (event === 'signedIn' || event === 'autoSignIn') {
        checkAuthState();
      } else if (event === 'signedOut') {
        setUser(null);
        window.user = null;
        window.authToken = null;
      }
    });

    // Initial check of the auth state
    checkAuthState();

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, []);

  const checkAuthState = async () => {
    try {
      console.log('Checking auth state...');
      
      // Check localStorage first for faster recovery
      const lastAuthUser = localStorage.getItem('CognitoIdentityServiceProvider.7123hmqco0fqs1j2hfd0kau4hs.LastAuthUser');
      if (!lastAuthUser) {
        throw new Error('No authenticated user in localStorage');
      }
      
      const idTokenKey = `CognitoIdentityServiceProvider.7123hmqco0fqs1j2hfd0kau4hs.${lastAuthUser}.idToken`;
      const idToken = localStorage.getItem(idTokenKey);
      
      if (!idToken) {
        throw new Error('No ID token found');
      }
      
      // Check if token is expired
      const payload = JSON.parse(atob(idToken.split('.')[1]));
      if (payload.exp * 1000 < Date.now()) {
        throw new Error('Token expired');
      }
      
      console.log('Valid tokens found in localStorage');
      
      // Try AWS Amplify first, but don't fail if it doesn't work
      let currentUser;
      let attributes = {};
      
      try {
        currentUser = await getCurrentUser();
        console.log('Current user from getCurrentUser:', currentUser);
        
        const fetchedAttributes = await fetchUserAttributes();
        attributes = fetchedAttributes || {};
        console.log('User attributes from fetchUserAttributes:', attributes);
      } catch (amplifyError) {
        console.log('AWS Amplify failed, using localStorage fallback:', amplifyError.message);
        
        // Create user object from localStorage
        currentUser = {
          username: lastAuthUser,
          userId: payload.sub,
          signInDetails: { loginId: payload.email }
        };
        
        // Extract attributes from JWT
        attributes = {
          sub: payload.sub,
          email: payload.email,
          given_name: payload.given_name,
          family_name: payload.family_name,
          'custom:business_id': payload['custom:business_id'],
          phone_number: payload.phone_number,
          email_verified: payload.email_verified,
          phone_number_verified: payload.phone_number_verified,
          'custom:beta_tester': payload['custom:beta_tester'] || 'true' // Default existing users to beta testers
        };
        
        console.log('Created user from localStorage:', currentUser);
        console.log('Extracted attributes from JWT:', attributes);
      }
      
      const userWithAttributes = { ...currentUser, attributes };
      
      console.log('🔍 AuthContext: Setting user state:', {
        userId: userWithAttributes?.userId,
        email: userWithAttributes?.attributes?.email,
        hasBusinessId: !!userWithAttributes?.attributes?.['custom:business_id'],
        emailVerified: userWithAttributes?.attributes?.email_verified
      });
      
      setUser(userWithAttributes);
      
      // Make user available globally for debugging
      window.user = userWithAttributes;
      window.authToken = idToken;
      window.checkAuthState = checkAuthState;
      window.loading = loading;
      
      // Debug function for signup flow issues
      window.debugSignupFlow = () => {
        console.log('=== SIGNUP FLOW DEBUG ===');
        console.log('Current user:', window.user);
        console.log('User email:', window.user?.attributes?.email);
        console.log('Email verified:', window.user?.attributes?.email_verified);
        console.log('Business ID:', window.user?.attributes?.['custom:business_id']);
        console.log('Current URL:', window.location.href);
        console.log('LocalStorage tokens:');
        const lastAuthUser = localStorage.getItem('CognitoIdentityServiceProvider.7123hmqco0fqs1j2hfd0kau4hs.LastAuthUser');
        console.log('Last auth user:', lastAuthUser);
        if (lastAuthUser) {
          const idTokenKey = `CognitoIdentityServiceProvider.7123hmqco0fqs1j2hfd0kau4hs.${lastAuthUser}.idToken`;
          const idToken = localStorage.getItem(idTokenKey);
          if (idToken) {
            const payload = JSON.parse(atob(idToken.split('.')[1]));
            console.log('JWT payload:', payload);
            console.log('Token expired?', payload.exp * 1000 < Date.now());
          }
        }
      };
      
      console.log('✅ Authentication successful:', userWithAttributes);
      
      // Log frontend auth event
      try {
        await logger.logFrontendAuth(userWithAttributes);
      } catch (logError) {
        console.warn('Failed to log auth event:', logError);
      }
    } catch (error) {
      console.log('❌ Authentication failed:', error.message);
      console.log('🔍 AuthContext: Clearing user state due to auth failure');
      setUser(null);
      window.user = null;
      window.authToken = null;
      window.checkAuthState = checkAuthState;
      window.loading = loading;
      
      // Debug function for signup flow issues
      window.debugSignupFlow = () => {
        console.log('=== SIGNUP FLOW DEBUG ===');
        console.log('Current user:', window.user);
        console.log('Current URL:', window.location.href);
        console.log('LocalStorage tokens:');
        const lastAuthUser = localStorage.getItem('CognitoIdentityServiceProvider.7123hmqco0fqs1j2hfd0kau4hs.LastAuthUser');
        console.log('Last auth user:', lastAuthUser);
        if (lastAuthUser) {
          const idTokenKey = `CognitoIdentityServiceProvider.7123hmqco0fqs1j2hfd0kau4hs.${lastAuthUser}.idToken`;
          const idToken = localStorage.getItem(idTokenKey);
          if (idToken) {
            const payload = JSON.parse(atob(idToken.split('.')[1]));
            console.log('JWT payload:', payload);
            console.log('Token expired?', payload.exp * 1000 < Date.now());
          }
        }
      };
    } finally {
      console.log('🔍 AuthContext: Setting loading to false');
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      console.log('🔍 Starting logout process');
      if (user) {
        await logger.logUserLogout(user);
      }

      // Sign out from AWS Amplify
      await signOut();

      console.log('🔍 Logout completed successfully');
    } catch (error) {
      console.error('Error signing out:', error);
    } finally {
      // Always clear the user state in the app, even if signOut fails.
      // Amplify's signOut should handle clearing localStorage.
      setUser(null);
      window.user = null;
      window.authToken = null;
    }
  };

  const updateUserProfile = async (attributes) => {
    try {
      console.log('🔍 updateUserProfile called with:', attributes);
      console.log('🔍 attributes type:', typeof attributes);
      console.log('🔍 attributes keys:', attributes ? Object.keys(attributes) : 'no keys');
      
      if (!attributes || typeof attributes !== 'object') {
        throw new Error('Invalid attributes provided');
      }
      
      // Filter out undefined, null, and empty string values
      const cleanAttributes = Object.fromEntries(
        Object.entries(attributes).filter(([key, value]) => 
          value !== undefined && value !== null && value !== ''
        )
      );
      
      console.log('🔍 cleanAttributes after filtering:', cleanAttributes);
      
      if (Object.keys(cleanAttributes).length === 0) {
        console.log('No valid attributes to update');
        return;
      }
      
      console.log('🔍 About to call updateUserAttributes with:', JSON.stringify(cleanAttributes, null, 2));
      
      try {
        // AWS Amplify v6 expects userAttributes in a specific format
        await updateUserAttributes({ userAttributes: cleanAttributes });
        console.log('🔍 updateUserAttributes completed successfully');
      } catch (amplifyError) {
        console.error('🔍 updateUserAttributes failed:', amplifyError);
        console.error('🔍 cleanAttributes that caused error:', cleanAttributes);
        throw amplifyError;
      }
      
      await checkAuthState(); // Refresh user data
    } catch (error) {
      console.error('Error updating user attributes:', error);
      throw error;
    }
  };



  const value = {
    user,
    loading,
    logout,
    updateUserProfile,
    checkAuthState
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};